import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    Box,
    Stack,
    Avatar,
    Typography, CardMedia, Button
} from "@mui/material";
import Slide from "@mui/material/Slide";
import { useSelector } from "react-redux";
import CustomButton from "../../../components/buttons/customButton";
import DeleteIcon from '@mui/icons-material/Delete';
import PhoneIcon from "@mui/icons-material/Phone";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReplyIcon from '@mui/icons-material/Reply';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import convoBg from '../../../assests/convoBg.jpg';
import { qrDialogPaperSx, QrModalHeader } from "../../qrCode/components/qrUi";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

const STATUS_COLORS = { approved: "#16a34a", rejected: "#dc2626", pending: "#d97706" };

// WhatsApp-style template button (under the bubble)
const waBtnSx = {
    justifyContent: "center",
    textTransform: "none",
    fontSize: 14,
    fontWeight: 600,
    color: "#027eb5",
    bgcolor: "#fff",
    borderRadius: 0,
    py: 0.9,
    borderTop: "1px solid #eef0f2",
    "&:hover": { bgcolor: "#f5f7f9" },
    "& .MuiButton-startIcon svg": { fontSize: 17 },
};

const mediaSx = {
    width: "100%",
    maxHeight: 240,
    objectFit: "cover",
    borderRadius: 1.5,
    display: "block",
    bgcolor: "#f1f5f9",
};

const footerBtnSx = {
    height: 40,
    px: 2.25,
    borderRadius: 2.5,
    textTransform: "none",
    fontSize: 14,
    fontWeight: 700,
    boxShadow: "none",
};

// Small info chip (status / category / language)
function InfoChip({ label, value, color, dot, mono }) {
    if (!value) return null;
    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ px: 1.25, py: 0.5, borderRadius: 5, bgcolor: `${color}10`, border: `1px solid ${color}2e`, minWidth: 0 }}
        >
            <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "#94a3b8" }}>
                {label}
            </Typography>
            {dot && <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />}
            <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 700, color, textTransform: mono ? "none" : "capitalize", fontFamily: mono ? "monospace" : "inherit" }}>
                {mono ? value : String(value).toLowerCase()}
            </Typography>
        </Stack>
    );
}

export default function ViewTemplateModal({ show, handleClose, rowData, handleDeleteTemplate }) {
    const { status } = useSelector((state) => state.templates);
    const { template_name, id, template_form, template_lang, template_category } = rowData;
    const templateStatus = rowData.status;
    const [tabValue, setTabValue] = useState(0);
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const formatText = (text) => {
        if (!text || typeof text !== 'string') {
            return '';
        }
        return text.replace(/\*([^*]+)\*/g, '<strong>$1</strong>').replace(/\\n/g, '\n')
    };

    // Same text, with {{1}} variables shown as small highlighted tags (display only)
    const formatPreview = (text) => formatText(text).replace(/\{\{(\d+)\}\}/g, '<span class="tpl-var">{{$1}}</span>');

    useEffect(() => {
        if (show) {
            setTabValue(0);
        }
    }, [show])

    const form = Array.isArray(template_form) ? template_form : [];
    const buttonsBlock = form.find((item) => item.type?.toLowerCase() === "buttons" && item.buttons?.length > 0);
    const statusColor = STATUS_COLORS[String(templateStatus || "").toLowerCase()] || "#475569";
    const previewTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    return (
        <>
            <Dialog
                open={show}
                onClose={handleClose}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                keepMounted
                PaperProps={{ sx: { ...qrDialogPaperSx, maxWidth: { sm: 480 } } }}
            >
                <QrModalHeader
                    icon={<DescriptionIcon />}
                    title="Template"
                    subtitle={template_name}
                    onClose={handleClose}
                />

                <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                    {/* Template info */}
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
                        <InfoChip label="Status" value={templateStatus} color={statusColor} dot />
                        <InfoChip label="Category" value={template_category} color="#7c3aed" />
                        <InfoChip label="Language" value={template_lang} color="#334155" mono />
                    </Stack>

                    {tabValue === 0 ? (
                        // Phone-style WhatsApp preview
                        <Box
                            role="tabpanel"
                            {...a11yProps(0)}
                            sx={{ borderRadius: 3.5, overflow: "hidden", border: "1px solid #e2e8f0", boxShadow: "0 10px 30px -18px rgba(15,23,42,0.45)" }}
                        >
                            {/* chat top bar */}
                            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 1.5, py: 1, bgcolor: "#075e54", color: "#fff" }}>
                                <Avatar sx={{ width: 34, height: 34, bgcolor: "#25d366", fontSize: 15, fontWeight: 700 }}>
                                    {(template_name || "T").charAt(0).toUpperCase()}
                                </Avatar>
                                <Box sx={{ minWidth: 0 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>Business</Typography>
                                        <VerifiedIcon sx={{ fontSize: 14, color: "#25d366" }} />
                                    </Stack>
                                    <Typography noWrap sx={{ fontSize: 11.5, opacity: 0.8 }}>{template_name}</Typography>
                                </Box>
                            </Stack>

                            {/* chat area */}
                            <Box
                                sx={{
                                    px: { xs: 1.5, sm: 2 },
                                    py: 2,
                                    minHeight: 200,
                                    bgcolor: "#efeae2",
                                    backgroundImage: `url(${convoBg})`,
                                    backgroundRepeat: "repeat",
                                    backgroundSize: "340px auto",
                                }}
                            >
                                {form.length > 0 ? (
                                    <Box
                                        sx={{
                                            position: "relative",
                                            maxWidth: 340,
                                            ml: 1,
                                            bgcolor: "#fff",
                                            borderRadius: 2,
                                            borderTopLeftRadius: 0,
                                            boxShadow: "0 1px 1px rgba(11,20,26,0.13)",
                                            // bubble tail
                                            "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0,
                                                left: -8,
                                                borderTop: "10px solid #fff",
                                                borderLeft: "8px solid transparent",
                                            },
                                            // {{1}} variable tags
                                            "& .tpl-var": {
                                                display: "inline-block",
                                                px: "5px",
                                                mx: "1px",
                                                borderRadius: "4px",
                                                fontSize: "0.85em",
                                                fontWeight: 700,
                                                lineHeight: 1.5,
                                                color: "#7c3aed",
                                                bgcolor: "#7c3aed14",
                                            },
                                        }}
                                    >
                                        <Box sx={{ p: 0.75, overflow: "hidden", borderRadius: 2, borderTopLeftRadius: 0 }}>
                                            {form.map((item, index) => {
                                                const type = item.type?.toLowerCase();
                                                const format = item.format?.toLowerCase();
                                                const headerHandle = item.example?.header_handle?.[0];

                                                return (
                                                    <React.Fragment key={index}>
                                                        {/* ===== Header ===== */}
                                                        {type === "header" && format === "text" && (
                                                            <Typography
                                                                sx={{ px: 0.75, pt: 0.5, fontSize: 14.5, fontWeight: 700, color: "#111b21", whiteSpace: "pre-line" }}
                                                                dangerouslySetInnerHTML={{ __html: formatPreview(item.text) }}
                                                            />
                                                        )}

                                                        {type === "header" && format === "image" && headerHandle && (
                                                            <CardMedia component="img" image={headerHandle} alt="Header Image" sx={mediaSx} />
                                                        )}

                                                        {type === "header" && format === "video" && headerHandle && (
                                                            <CardMedia component="video" controls sx={mediaSx}>
                                                                <source src={headerHandle} type="video/mp4" />
                                                            </CardMedia>
                                                        )}

                                                        {type === "header" && format === "document" && headerHandle && (
                                                            <Stack
                                                                component="a"
                                                                href={headerHandle}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                direction="row"
                                                                alignItems="center"
                                                                spacing={1}
                                                                sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "#f5f6f6", color: "#111b21", textDecoration: "none", "&:hover": { bgcolor: "#eef0f1" } }}
                                                            >
                                                                <InsertDriveFileOutlinedIcon sx={{ color: "#dc2626" }} />
                                                                <Typography variant="body2" fontWeight={600}>View Document</Typography>
                                                            </Stack>
                                                        )}

                                                        {/* ===== Body ===== */}
                                                        {type === "body" && (
                                                            <Typography
                                                                sx={{ px: 0.75, pt: 0.75, fontSize: 14, lineHeight: 1.55, color: "#111b21", whiteSpace: "pre-line", wordBreak: "break-word" }}
                                                                dangerouslySetInnerHTML={{ __html: formatPreview(item.text) }}
                                                            />
                                                        )}

                                                        {/* ===== Footer ===== */}
                                                        {type === "footer" && (
                                                            <Typography
                                                                sx={{ px: 0.75, pt: 0.5, display: "block", fontSize: 12.5, color: "#667781" }}
                                                                dangerouslySetInnerHTML={{ __html: formatText(item.text) }}
                                                            />
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}

                                            {/* time + ticks */}
                                            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.4} sx={{ px: 0.75, pt: 0.25 }}>
                                                <Typography sx={{ fontSize: 11, color: "#667781" }}>{previewTime}</Typography>
                                                <DoneAllIcon sx={{ fontSize: 15, color: "#53bdeb" }} />
                                            </Stack>
                                        </Box>

                                        {/* ===== Buttons ===== */}
                                        {buttonsBlock && (
                                            <Stack sx={{ overflow: "hidden", borderBottomLeftRadius: 8, borderBottomRightRadius: 8 }}>
                                                {buttonsBlock.buttons.map((button, btnIndex) => (
                                                    <React.Fragment key={btnIndex}>
                                                        {button.type === "url" && (
                                                            <Button fullWidth href={button.url} target="_blank" rel="noopener noreferrer" startIcon={<OpenInNewIcon />} sx={waBtnSx}>
                                                                {button.text}
                                                            </Button>
                                                        )}
                                                        {button.type === "PHONE_NUMBER" && (
                                                            <Button fullWidth href={`tel:${button.phone_number}`} startIcon={<PhoneIcon />} sx={waBtnSx}>
                                                                {button.text}
                                                            </Button>
                                                        )}
                                                        {button.type === "QUICK_REPLY" && (
                                                            <Button fullWidth startIcon={<ReplyIcon />} sx={waBtnSx}>
                                                                {button.text}
                                                            </Button>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </Stack>
                                        )}
                                    </Box>
                                ) : (
                                    <Typography sx={{ textAlign: "center", py: 4, color: "#667781", fontSize: 14 }}>
                                        No template form data
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        // Delete confirmation
                        <Stack
                            role="tabpanel"
                            {...a11yProps(1)}
                            alignItems="center"
                            spacing={1.25}
                            sx={{ py: 3.5, px: 2, textAlign: "center", borderRadius: 3, bgcolor: "#fef2f2", border: "1px solid #fecaca" }}
                        >
                            <Box sx={{ width: 56, height: 56, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#fee2e2", color: "#dc2626", boxShadow: "0 0 0 6px #fef2f2, 0 0 0 7px #fecaca" }}>
                                <WarningAmberRoundedIcon />
                            </Box>
                            <Typography fontWeight={800} color="#0f172a" sx={{ pt: 0.5 }}>Delete this template?</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word", maxWidth: 340 }}>
                                Are you sure you want to delete <b>"{template_name}"</b>? This cannot be undone.
                            </Typography>
                        </Stack>
                    )}
                </DialogContent>

                {/* Footer actions */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ px: { xs: 1.5, sm: 2.5 }, py: 1.5, borderTop: "1px solid #e9edf2", bgcolor: "#fafbfc" }}
                >
                    {tabValue === 0 ? (
                        <>
                            <Button
                                onClick={(e) => handleTabChange(e, 1)}
                                startIcon={<DeleteOutlineIcon />}
                                sx={{
                                    ...footerBtnSx,
                                    color: "#dc2626",
                                    bgcolor: "#fef2f2",
                                    border: "1.5px solid #fecaca",
                                    transition: "all .15s ease",
                                    "& .MuiButton-startIcon svg": { fontSize: 19 },
                                    "&:hover": {
                                        color: "#fff",
                                        bgcolor: "#dc2626",
                                        borderColor: "#dc2626",
                                        boxShadow: "0 8px 18px -8px rgba(220,38,38,0.7)",
                                    },
                                }}
                            >
                                Delete Template
                            </Button>
                            <Button
                                onClick={handleClose}
                                sx={{ ...footerBtnSx, color: "#475569", bgcolor: "#fff", border: "1.5px solid #e2e8f0", "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" } }}
                            >
                                Close
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                onClick={(e) => handleTabChange(e, 0)}
                                sx={{ ...footerBtnSx, color: "#475569", bgcolor: "#fff", border: "1.5px solid #e2e8f0", "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" } }}
                            >
                                Cancel
                            </Button>
                            <Box sx={{ "& .MuiButton-root": { ...footerBtnSx, bgcolor: "#dc2626", color: "#fff", "&:hover": { bgcolor: "#b91c1c" } } }}>
                                <CustomButton
                                    status={status}
                                    onClick={() => handleDeleteTemplate(id)}
                                    label="Delete"
                                    loadingLabel="Deleting..."
                                    icon={<DeleteIcon />}
                                />
                            </Box>
                        </>
                    )}
                </Stack>
            </Dialog>
        </>
    );
}
